import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { areasApi } from '../../api/areas.js';
import { usersApi } from '../../api/users.js';
import { citiesApi } from '../../api/cities.js';

function SearchableSelect({ options, value, onChange, placeholder, disabled, openUp }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const selectedOption = options.find(o => o.value?.toString() === value?.toString());

  const filteredOptions = options
    .filter(o => o.label.toLowerCase().includes(debouncedQuery.toLowerCase()))
    .slice(0, 20);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div
        className={`form-control ${disabled ? 'disabled' : ''}`}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          minHeight: '38px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: disabled ? '#f9fafb' : '#fff'
        }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
          {selectedOption ? selectedOption.label : <span style={{ color: '#9ca3af' }}>{placeholder}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {selectedOption && !disabled && (
            <span
              style={{ fontSize: 14, color: '#94a3b8', padding: '0 4px', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setQuery('');
                setIsOpen(false);
              }}
              title="Clear selection"
            >
              ✕
            </span>
          )}
          <span style={{ fontSize: 10, color: '#94a3b8' }}>▼</span>
        </div>
      </div>

      {isOpen && !disabled && (
        <div style={{
          position: 'absolute',
          bottom: openUp ? '100%' : 'auto',
          top: openUp ? 'auto' : '100%',
          left: 0,
          right: 0,
          zIndex: 50,
          background: '#fff',
          border: '1px solid var(--border)',
          borderRadius: 6,
          marginBottom: openUp ? 4 : 0,
          marginTop: openUp ? 0 : 4,
          boxShadow: '0 -4px 6px -1px rgba(0,0,0,0.1), 0 4px 6px -1px rgba(0,0,0,0.1)'
        }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Type to search..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto', padding: '4px 0' }}>
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 13 }}>No results found</div>
            ) : (
              filteredOptions.map(opt => (
                <div
                  key={opt.value}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: 13,
                    background: opt.value?.toString() === value?.toString() ? 'var(--primary-light)' : 'transparent'
                  }}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                    setQuery('');
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-light)'}
                  onMouseLeave={e => e.currentTarget.style.background = opt.value?.toString() === value?.toString() ? 'var(--primary-light)' : 'transparent'}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Area Form Modal ──────────────────────────────────────────────────────────
function AreaModal({ area, onClose, onSave }) {
  const [name, setName]             = useState(area?.name || '');
  const [description, setDesc]      = useState(area?.description || '');
  const [parentId, setParentId]     = useState(area?.parent_id?.toString() || '');
  const [cityId, setCityId]         = useState(area?.city_id?.toString() || area?.city?.id?.toString() || '');
  const [saving, setSaving]         = useState(false);
  const [err, setErr]               = useState('');
  const [cities, setCities]         = useState([]);
  const [parentAreas, setParents]   = useState([]);

  useEffect(() => {
    Promise.all([
      citiesApi.getAll(),
      areasApi.getAll({ parent_id: 'null', limit: 200 }),
    ]).then(([cRes, pRes]) => {
      setCities(cRes.data || []);
      setParents(pRes.data ?? []);
    }).catch(console.error);
  }, []);

  const handleSave = async () => {
    if (!name.trim()) { setErr('Area name is required.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({
        name:        name.trim(),
        description: description.trim() || null,
        parent_id:   parentId || null,
        city_id:     cityId   || null,
      });
    } catch (e) { setErr(e.message || 'Save failed.'); setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 520, maxWidth: '90vw' }}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>{area ? 'Edit Area' : 'Create New Area'}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && (
            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>
              {err}
            </div>
          )}
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label" style={{ fontWeight: 600, fontSize: 13 }}>Area Name *</label>
            <input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Park Street" />
          </div>
          <div className="grid-2" style={{ marginBottom: 14 }}>
            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 600, fontSize: 13 }}>Parent Area</label>
              <select className="form-control" value={parentId} onChange={e => setParentId(e.target.value)}>
                <option value="">None (top-level)</option>
                {parentAreas.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 600, fontSize: 13 }}>City</label>
              <select className="form-control" value={cityId} onChange={e => setCityId(e.target.value)}>
                <option value="">Select City</option>
                {cities.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: 600, fontSize: 13 }}>Description / Coverage Details</label>
            <textarea className="form-control" style={{ minHeight: 70, resize: 'vertical' }} value={description} onChange={e => setDesc(e.target.value)} placeholder="Streets, boundaries or region notes…" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Area'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Assign Sales Modal ───────────────────────────────────────────────────────
function AssignSalesModal({ area, onClose, onSave }) {
  const [allUsers, setAllUsers] = useState([]);
  const [selected, setSelected] = useState(new Set(area.isBulk ? [] : (area.users || []).map(u => u.id)));
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await usersApi.getAll({
          role: 'sales,sales_tl',
          limit: 20,
          search: debouncedSearch,
          is_active: true
        });
        setAllUsers(res.data ?? []);
      } catch { setErr('Failed to load sales users.'); }
      finally { setLoading(false); }
    })();
  }, [debouncedSearch]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleSave = async () => {
    setSaving(true); setErr('');
    try { await onSave([...selected]); }
    catch (e) { setErr(e.message || 'Save failed.'); setSaving(false); }
  };

  const filtered = allUsers;


  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>
            {area.isBulk ? `Assign Sales to: ${area.count} selected areas` : `Assign Sales to: ${area.name}`}
          </span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && (
            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>
              {err}
            </div>
          )}
          <div className="search-bar" style={{ marginBottom: 12 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
            <input placeholder="Search sales users…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontStyle: 'italic' }}>No sales users found.</div>
          ) : (
            <div style={{ maxHeight: 300, overflowY: 'auto', border: '1.5px solid var(--border)', borderRadius: 8, padding: '8px 14px', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map(u => {
                const role = u.role?.name || u.Role?.name || '';
                const label = role === 'sales_tl' ? 'TL' : 'Sales';
                return (
                  <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={selected.has(u.id)} style={{ width: 15, height: 15, accentColor: 'var(--primary)' }} onChange={() => toggle(u.id)} />
                    <div>
                      <span style={{ fontWeight: 600 }}>{u.name}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 6 }}>{u.email}</span>
                    </div>
                    <span className={`badge badge-${label === 'TL' ? 'primary' : 'gray'}`} style={{ marginLeft: 'auto', fontSize: 10 }}>{label}</span>
                  </label>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>
            {selected.size} user{selected.size !== 1 ? 's' : ''} selected
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save Assignments'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AreasPage() {
  const location = useLocation();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  // Deep-link from Staffs' area badges (see StaffsPage.jsx's goToArea) —
  // arriving with a target area name pre-fills the search box so this page
  // opens already filtered down to it, rather than the full unfiltered list.
  const [search, setSearch] = useState(location.state?.search || '');
  const debouncedSearch = useDebounce(search);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(20);

  const [selected, setSelected] = useState(new Set());
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [editArea, setEditArea] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);

  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [parentFilter, setParentFilter] = useState('All Regions');
  const [parentAreas, setParentAreas] = useState([]);

  const fetchAreas = async (p = page, q = search, parentId = parentFilter) => {
    setLoading(true);
    try {
      const params = { page: p, limit: limit === 100000 ? 100000 : limit, search: q || undefined };
      if (parentId !== 'All Regions' && parentId) {
        params.parent_id = parentId;
      }
      const res = await areasApi.getAll(params);
      setAreas(res.data ?? []);
      setTotalPages(res.totalPages || 1);
      setTotalItems(res.count || 0);
    } catch (e) {
      console.error('Failed to load areas:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    areasApi.getAll({ parent_id: 'null', limit: 200 })
      .then(res => {
        setParentAreas(res.data ?? []);
      })
      .catch(console.error);
  }, []);

  useEffect(() => { setPage(1); setSelected(new Set()); }, [debouncedSearch, parentFilter, limit]);
  useEffect(() => { fetchAreas(page, debouncedSearch, parentFilter); }, [page, debouncedSearch, parentFilter, limit]);

  // ── Checkbox helpers ──────────────────────────────────────────────────────
  const allChecked = areas.length > 0 && areas.every(a => selected.has(a.id));
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(areas.map(a => a.id)));
  const toggleOne = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleSaveArea = async (payload) => {
    if (editArea) {
      await areasApi.update(editArea.id, payload);
      setMsg({ type: 'success', text: 'Area updated successfully!' });
    } else {
      await areasApi.create(payload);
      setMsg({ type: 'success', text: 'Area created successfully!' });
    }
    setShowAreaModal(false);
    setEditArea(null);
    fetchAreas(page, debouncedSearch);
  };

  const handleDelete = async (area) => {
    if (!window.confirm(`Delete area "${area.name}"?`)) return;
    try {
      await areasApi.remove(area.id);
      setMsg({ type: 'success', text: 'Area deleted.' });
      fetchAreas(page, debouncedSearch);
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Delete failed.' });
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected area(s)? This cannot be undone.`)) return;
    try {
      await areasApi.bulkDelete([...selected]);
      setSelected(new Set());
      setMsg({ type: 'success', text: `${selected.size} area(s) deleted.` });
      fetchAreas(page, debouncedSearch);
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Bulk delete failed.' });
    }
  };

  // ── Assign Sales ──────────────────────────────────────────────────────────
  const handleAssignSave = async (userIds) => {
    if (assignTarget.isBulk) {
      await areasApi.bulkAssign(assignTarget.areaIds, userIds);
      setMsg({ type: 'success', text: `Assignments saved for ${assignTarget.count} areas.` });
      setSelected(new Set());
    } else {
      await areasApi.assignUsers(assignTarget.id, userIds);
      setMsg({ type: 'success', text: `Assignments saved for "${assignTarget.name}".` });
    }
    setShowAssignModal(false);
    setAssignTarget(null);
    fetchAreas(page, debouncedSearch);
  };

  const handleBulkAssignClick = () => {
    setAssignTarget({
      isBulk: true,
      areaIds: Array.from(selected),
      count: selected.size
    });
    setShowAssignModal(true);
  };

  // ── Excel Import ──────────────────────────────────────────────────────────
  const handleExcelImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64String = event.target.result.split(',')[1];
      setImporting(true);
      setImportResults(null);
      setMsg({ type: '', text: '' });
      try {
        const res = await areasApi.import({ file: base64String });
        if (res.success) {
          setImportResults(res.data);
          setMsg({ type: 'success', text: `Import complete! ${res.data.successCount} succeeded, ${res.data.failedCount} failed.` });
          fetchAreas(page, debouncedSearch);
        } else {
          setMsg({ type: 'error', text: res.error || 'Import failed' });
        }
      } catch (err) {
        setMsg({ type: 'error', text: err.message || 'Import failed' });
      } finally {
        setImporting(false);
        e.target.value = '';
      }
    };
    reader.onerror = () => setMsg({ type: 'error', text: 'Failed to read file.' });
    reader.readAsDataURL(file);
  };

  return (
    <div className="page-body">
      {showAreaModal && (
        <AreaModal
          area={editArea}
          onClose={() => { setShowAreaModal(false); setEditArea(null); }}
          onSave={handleSaveArea}
        />
      )}
      {showAssignModal && assignTarget && (
        <AssignSalesModal
          area={assignTarget}
          onClose={() => { setShowAssignModal(false); setAssignTarget(null); }}
          onSave={handleAssignSave}
        />
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="card-title">Geographical Areas</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {selected.size > 0 && (
              <>
                <button className="btn btn-xs" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }} onClick={handleBulkAssignClick}>
                  Assign Sales ({selected.size})
                </button>
                <button className="btn btn-xs" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }} onClick={handleBulkDelete}>
                  Delete {selected.size} selected
                </button>
              </>
            )}
            <label className="btn btn-outline btn-xs" style={{ cursor: 'pointer', margin: 0, display: 'inline-flex', alignItems: 'center' }}>
              {importing ? 'Importing…' : 'Import Excel'}
              <input type="file" accept=".xlsx, .xls" onChange={handleExcelImport} style={{ display: 'none' }} disabled={importing} />
            </label>
            <button className="btn btn-primary btn-xs" onClick={() => { setEditArea(null); setShowAreaModal(true); }}>+ Add Area</button>
          </div>
        </div>

        <div className="card-body">
          {/* Notifications */}
          {msg.text && (
            <div style={{
              marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
              background: msg.type === 'error' ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${msg.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
              color: msg.type === 'error' ? '#dc2626' : '#16a34a'
            }}>
              {msg.text}
              <button onClick={() => setMsg({ type: '', text: '' })} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 700 }}>✕</button>
            </div>
          )}

          {/* Import Report */}
          {importResults && (
            <div style={{ marginBottom: 20, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-main)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Excel Import Report</h4>
                <button type="button" className="btn btn-xs btn-outline" style={{ padding: '2px 6px', fontSize: 10 }} onClick={() => setImportResults(null)}>Clear</button>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 12 }}>
                Rows: <strong>{importResults.total}</strong> | Success: <strong style={{ color: 'var(--success)' }}>{importResults.successCount}</strong> | Failed: <strong style={{ color: 'var(--danger)' }}>{importResults.failedCount}</strong>
              </div>
              {importResults.errors.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 11, color: '#dc2626', marginBottom: 4 }}>Errors ({importResults.errors.length}):</div>
                  <div style={{ maxHeight: 100, overflowY: 'auto', background: '#fff', border: '1px solid #fee2e2', borderRadius: 6, padding: '8px 12px' }}>
                    {importResults.errors.map((e, i) => <div key={i} style={{ fontSize: 11, color: '#b91c1c', fontFamily: 'monospace', lineHeight: 1.5 }}>• {e}</div>)}
                  </div>
                </div>
              )}
              {importResults.logs.length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 11, color: '#16a34a', marginBottom: 4 }}>Success ({importResults.logs.length}):</div>
                  <div style={{ maxHeight: 100, overflowY: 'auto', background: '#fff', border: '1px solid #dcfce7', borderRadius: 6, padding: '8px 12px' }}>
                    {importResults.logs.map((l, i) => <div key={i} style={{ fontSize: 11, color: '#15803d', fontFamily: 'monospace', lineHeight: 1.5 }}>• {l}</div>)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Search bar */}
          <div className="filters-row" style={{ marginBottom: 12 }}>
            <div className="search-bar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input placeholder="Search areas…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div style={{ width: 200, marginLeft: 12 }}>
              <SearchableSelect
                options={parentAreas.map(pa => ({ value: pa.id, label: pa.name }))}
                value={parentFilter === 'All Regions' ? '' : parentFilter}
                onChange={val => {
                  setParentFilter(val ? val.toString() : 'All Regions');
                  setPage(1);
                }}
                placeholder="All Regions"
              />
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading areas…</div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input type="checkbox" checked={allChecked} onChange={toggleAll} style={{ width: 15, height: 15, accentColor: 'var(--primary)', cursor: 'pointer' }} />
                    </th>
                    <th>ID</th>
                    <th>Area Name</th>
                    <th>Parent Area</th>
                    <th>City</th>
                    <th>Assigned Sales</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {areas.length === 0 ? (
                    <tr><td colSpan="8" style={{ textAlign: 'center', padding: 28, color: 'var(--text-muted)' }}>No areas found.</td></tr>
                  ) : areas.map(area => (
                    <tr key={area.id}>
                      <td>
                        <input type="checkbox" checked={selected.has(area.id)} onChange={() => toggleOne(area.id)} style={{ width: 15, height: 15, accentColor: 'var(--primary)', cursor: 'pointer' }} />
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>#{area.id}</td>
                      <td>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{area.name}</span>
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                        {area.parent?.name || (area.parent_id ? `#${area.parent_id}` : '—')}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {area.city?.name
                          ? <span className="badge badge-secondary">{area.city.name}</span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td>
                        {area.users && area.users.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {area.users.slice(0, 3).map(u => (
                              <span key={u.id} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', borderRadius: 12, padding: '2px 8px', fontSize: 11, fontWeight: 500 }}>
                                {u.name}
                              </span>
                            ))}
                            {area.users.length > 3 && (
                              <span style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b', borderRadius: 12, padding: '2px 8px', fontSize: 11 }}>
                                +{area.users.length - 3} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 12 }}>None</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-outline btn-xs" onClick={() => { setEditArea(area); setShowAreaModal(true); }}>Edit</button>
                          <button className="btn btn-outline btn-xs" style={{ color: 'var(--primary)' }} onClick={() => { setAssignTarget(area); setShowAssignModal(true); }}>Assign Sales</button>
                          <button className="btn btn-xs" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: 'none' }} onClick={() => handleDelete(area)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalItems > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Show:</span>
                <select
                  className="form-control"
                  style={{ width: 'auto', padding: '4px 8px', fontSize: 13, cursor: 'pointer', height: 'auto' }}
                  value={limit === 100000 ? 'all' : limit}
                  onChange={e => { const v = e.target.value; setLimit(v === 'all' ? 100000 : parseInt(v, 10)); setPage(1); }}
                >
                  <option value={20}>20</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                  <option value="all">All</option>
                </select>
              </div>
              <button
                className="btn btn-outline btn-xs"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(p - 1, 1))}
                style={{ padding: '6px 14px', borderRadius: 6, cursor: page === 1 ? 'not-allowed' : 'pointer' }}
              >
                Previous
              </button>
              <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>
                Page {page} of {totalPages} <span style={{ color: '#94a3b8', margin: '0 4px' }}>·</span> {totalItems} entries
              </span>
              <button
                className="btn btn-outline btn-xs"
                disabled={page === totalPages}
                onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                style={{ padding: '6px 14px', borderRadius: 6, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
